import { useQuery } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "design-system/atoms/Tooltip"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "design-system/molecules/Tabs"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { encode } from "lopermut"
import { ArrowUpRight } from "lucide-react"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useShopifyCampaignWidgetMutation } from "modules/campaigns/hooks/useShopifyCampaignWidgetMutation"
import { shopifyIntegrationQuery } from "modules/settings/lib/shopify/shopifyIntegrationQuery"
import { Trans, useTranslation } from "react-i18next"
import { CampaignDetailResponseDataSchema } from "schema"
import { z } from "zod"

type Props = {
  campaign: z.infer<typeof CampaignDetailResponseDataSchema>
}

export function WidgetMetadataInfo({ campaign }: Props) {
  const { t } = useTranslation()
  const { data } = useAuthEmployeeQuery()
  const { data: shopifyIntegration } = useQuery(shopifyIntegrationQuery())
  const { mutate, isPending } = useShopifyCampaignWidgetMutation()

  const code = encode(`${campaign.id}-${data?.companyId}`)
  const isShopifyWidgetActive = shopifyIntegration?.activeWidgetCampaignId === campaign.id
  const hasShopifyIntegration = Boolean(shopifyIntegration?.shopifyUrl)

  const basicScript = `<script
  type="module"
  src="https://app.permut.com/widget.js"
  defer
  data-permut="${code}">
</script>`

  const googleTagManagerScript = `<script>
  (function () {
    var script = document.createElement("script")
    script.type = "module"
    script.src = "https://app.permut.com/widget.js"
    script.dataset.permut = "${code}"
    document.body.appendChild(script)
  })()
</script>`

  const completeExample = `<script
  type="module"
  src="https://app.permut.com/widget.js"
  defer
  data-locale="en-US"
  data-color="#000000"
  data-permut="${code}">
</script>

<script>
  // window.PermutWidget is available once the widget loads
  PermutWidget.identifyUser({
    email: "customer@example.com",
    phone: "+1234567890",
    name: "John Doe",
    metadata: {
      "Company Name": "Acme Corp",
      "Subscription Tier": "Premium",
      "Plan": "Enterprise",
      "Custom Field": "Any value"
    }
  })
</script>`

  const basicHtml = `\`\`\`html
${basicScript}
\`\`\`
`

  const googleTagManagerHtml = `\`\`\`html
${googleTagManagerScript}
\`\`\`
`

  const completeHtml = `\`\`\`html
${completeExample}
\`\`\`
`

  return (
    <div className="flex flex-col gap-4">
      <span className="text-sm text-muted-foreground">
        <Trans
          i18nKey={"campaigns.trigger.metadata.widget.description"}
          components={{
            a: (
              <Link
                className="underline text-muted-foreground"
                to="/settings/channels/widget"
                target="_blank"
              />
            ),
          }}
        />
      </span>

      {campaign.uuid && (
        <>
          {hasShopifyIntegration && (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">
                  {t("campaigns.trigger.metadata.widget.shopifyInstall.title")}
                </span>
                <span className="text-sm text-muted-foreground">
                  <Trans
                    i18nKey={"campaigns.trigger.metadata.widget.shopifyInstall.description"}
                    components={{
                      a: (
                        <a
                          className="underline text-muted-foreground"
                          href="https://apps.shopify.com/permutai"
                          target="_blank"
                          rel="noreferrer"
                        />
                      ),
                    }}
                  />
                </span>
                <span className="text-xs text-muted-foreground">
                  {t("campaigns.trigger.metadata.widget.shopifyInstall.activationHelp")}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="w-fit">
                        <Button
                          type="button"
                          disabled={isShopifyWidgetActive || isPending}
                          onClick={() => mutate({ campaignId: campaign.id })}
                        >
                          {t("campaigns.trigger.metadata.widget.shopifyInstall.button")}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {isShopifyWidgetActive && (
                      <TooltipContent>
                        {t("campaigns.trigger.metadata.widget.shopifyInstall.activeTooltip")}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>

                {shopifyIntegration?.themeEditorUrl && (
                  <Button asChild type="button" variant="outline">
                    <a href={shopifyIntegration.themeEditorUrl} target="_blank" rel="noreferrer">
                      <span>
                        {t("campaigns.trigger.metadata.widget.shopifyInstall.openThemeEditor")}
                      </span>
                      <ArrowUpRight />
                    </a>
                  </Button>
                )}
              </div>
            </div>
          )}

          <Tabs defaultValue="basic" className="gap-4">
            <TabsList className="h-auto gap-6 bg-background p-0">
              <TabsTrigger value="basic" className="px-0 py-2">
                {t("campaigns.trigger.metadata.widget.basicInstall")}
              </TabsTrigger>
              <TabsTrigger value="googleTagManager" className="px-0 py-2">
                {t("campaigns.trigger.metadata.widget.googleTagManager")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="basic">
              <MarkdownRenderer className="text-sm **:break-all **:whitespace-pre-wrap">
                {basicHtml}
              </MarkdownRenderer>
            </TabsContent>
            <TabsContent value="googleTagManager">
              <MarkdownRenderer className="text-sm **:break-all **:whitespace-pre-wrap">
                {googleTagManagerHtml}
              </MarkdownRenderer>
            </TabsContent>
          </Tabs>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">
              {t("campaigns.trigger.metadata.widget.optionalTitle")}
            </span>
            <div className="text-sm text-muted-foreground space-y-1">
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">{"data-locale"}</code>
                  {" - "}
                  {t("campaigns.trigger.metadata.widget.localeAttribute")}
                </li>
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">{"data-color"}</code>
                  {" - "}
                  {t("campaigns.trigger.metadata.widget.colorAttribute")}
                </li>
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">
                    {"PermutWidget.identifyUser()"}
                  </code>
                  {" - "}
                  {t("campaigns.trigger.metadata.widget.identifyUser")}
                </li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">
              {t("campaigns.trigger.metadata.widget.completeExample")}
            </span>
            <MarkdownRenderer className="text-sm **:break-all **:whitespace-pre-wrap">
              {completeHtml}
            </MarkdownRenderer>
            <span className="text-xs text-muted-foreground">
              {t("campaigns.trigger.metadata.widget.metadataNote")}
            </span>
          </div>
        </>
      )}
    </div>
  )
}
